import { Color } from '@persona-id/design-tokens';
import { isEmpty, startCase } from 'lodash/fp';
import {
  ComponentProps,
  FC,
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { AxisInterval, BaseAxisProps, DataKey } from 'recharts/types/util/types';
import { ThemeContext } from 'styled-components';

import { ChartLegendContent } from 'dashboard/components/BarAndLineChartWithLegend/ChartLayout';
import ChartLayout, {
  ChartTooltipContainer,
  ChartTooltipHeader,
  ChartTooltipLabel,
  ChartTooltipLabelContainer,
} from 'dashboard/layouts/ChartLayout';
import { formatNumber, formatNumberLarge } from 'dashboard/lib/utils/formatters';
import { BASE } from 'design-system/variables/spacing';
import { TransitionDuration } from 'design-system/variables/transitions';
import Dot from 'images/dashboard/dot.svg';
import useToggle from 'lib/hooks/useToggle';

export interface RenderLabelProps {
  x: number;
  y: number;
  fill: string;
  value: unknown;
  index: number;
  width: number;
  height: number;
}

// TODO: this is likely too specialized to be a component. Move into
// front-end/dashboard/views/DashboardInquiryAnalyticsVolumeV2View
export interface BarAndLineChartWithLegendProps {
  data: Record<any, any>[];
  dataKeys?: DataKey<any>[];
  colors: string[];
  stackColors?: string[];
  barKeys: string[];
  barsLabel?: string;
  lineKeys: string[];
  linesLabel?: string;
  lineGroups?: Record<any, any>;
  stackedBars?: boolean;
  // need chartWidth for testing - https://stackoverflow.com/questions/60215153/when-mounted-in-enzyme-why-does-a-recharts-chart-with-a-responsivecontainer-not
  chartWidth?: number;
  interval?: AxisInterval;

  createTooltipHeader: (label: string | number) => string;
  legendFormatter?: (value: string) => string;
  onDeepLink?: (datum: Record<any, any>, keys: string[]) => void;
  renderTooltip?: (hoveredKey: string) => ReactNode;
  legendHeight?: number | 'auto';
  keyFormatter?: (value: string) => string;
  hideLegend?: boolean;
  renderBarLabel?: (props: RenderLabelProps, idx: number) => ReactNode;
  chartHeight?: number;
  numbersAxisFormatter?: (value: number) => string;

  // x-axis options
  xAxisHeight?: number;
  xAxisLabel?: string;
  xAxisTextAnchor?: string;
  xAxisAngle?: number;
  xDataKey: string | ((data: unknown) => string);
  xFormatter?: (value: any, index: number) => string;
  xAxisTick?: BaseAxisProps['tick'];

  // y-axis options
  yAxisTicks?: (number | string)[];
  percentageAxisDomain?: [number | string, number | string];

  barIsAnimationActive?: boolean;
  initialFilteredLegendKeys?: string[];
  showZeros?: boolean;
  scrollEnabled?: boolean;
  legendIcons?: Record<string, ReactElement>;
  legendNumberOpts?: Intl.NumberFormatOptions;
  allowEscapeViewBoxX?: boolean;
}

const DIMENSIONS = {
  height: 290,
  margin: {
    top: BASE * 4,
    left: -BASE * 2,
  },
};

export const BarAndLineChartWithLegend = ({
  data,
  barKeys,
  barsLabel = 'Volume',
  lineKeys,
  linesLabel = 'Rate',
  lineGroups,
  stackedBars = false,
  colors,
  stackColors,
  chartWidth,
  createTooltipHeader,
  legendFormatter = startCase,
  onDeepLink,
  renderTooltip,
  legendHeight,
  keyFormatter,
  hideLegend, // x-axis
  xFormatter,
  xAxisLabel,
  xDataKey,
  xAxisHeight = 30,
  xAxisTextAnchor = 'middle', // passing undefined textAnchor to XAxis causes the behavior to change from the default ('middle') to 'start'
  xAxisAngle,
  xAxisTick,
  renderBarLabel,
  // y-axis
  yAxisTicks,
  barIsAnimationActive,
  chartHeight,
  initialFilteredLegendKeys,
  showZeros,
  scrollEnabled = true,
  legendIcons = {},
  legendNumberOpts = {},
  percentageAxisDomain,
  allowEscapeViewBoxX = true,
  numbersAxisFormatter,
  interval = 0,
}: BarAndLineChartWithLegendProps) => {
  const theme = useContext(ThemeContext);

  const [filteredLegendKeys, setFilteredLegendKeys] = useState(
    initialFilteredLegendKeys == null ? [] : [...initialFilteredLegendKeys],
  );
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [legendHovered, { enable: setLegendHovered, disable: clearLegendHovered }] = useToggle();

  const handleLegendClick = useCallback(
    ({ dataKey }: Payload) =>
      typeof dataKey === 'string' &&
      setFilteredLegendKeys(
        filteredLegendKeys.includes(dataKey)
          ? filteredLegendKeys.filter((s) => s !== dataKey)
          : filteredLegendKeys.concat(dataKey),
      ),
    [filteredLegendKeys, data, initialFilteredLegendKeys],
  );

  // handle a reload of the initialFilteredLegendKeys
  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setFilteredLegendKeys(initialFilteredLegendKeys);
  }, [initialFilteredLegendKeys]);

  const handleLegendMouseEnter = useCallback(({ dataKey }: Payload) => {
    setLegendHovered();
    typeof dataKey === 'string' && setHoveredKey(dataKey);
  }, []);

  const handleLegendMouseLeave = useCallback(() => {
    clearLegendHovered();
    setHoveredKey(null);
  }, []);

  const handleDeepLink = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (_: unknown, index: number) => onDeepLink(data[index], filteredLegendKeys),
    [onDeepLink, data, filteredLegendKeys],
  );

  const tooltipContent = renderTooltip ? (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => renderTooltip(hoveredKey)
  ) : (
    <TooltipContent
      createHeader={createTooltipHeader}
      hoveredKey={hoveredKey}
      legendFormatter={legendFormatter}
      keyToUnit={(key) => (lineKeys.includes(key) ? '%' : '')}
      showZeros={showZeros}
      legendNumberOpts={legendNumberOpts}
    />
  );

  const effectiveChartHeight = chartHeight ?? DIMENSIONS.height;
  const totalHeight = effectiveChartHeight + xAxisHeight;

  // When we legendHeight is undefined the ChartContentLegend is automatically sized
  const effectiveLegendHeight =
    legendHeight === 'auto' ? undefined : (legendHeight ?? effectiveChartHeight);
  return (
    <ChartLayout data-test='bar-and-line-chart'>
      <ResponsiveContainer height={totalHeight} width={chartWidth || '100%'}>
        <ComposedChart data={data} margin={DIMENSIONS.margin}>
          <CartesianGrid vertical={false} stroke={theme.color.border.secondary} />
          <XAxis
            dataKey={xDataKey}
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={xFormatter}
            tick={
              xAxisTick ?? {
                fontSize: theme.text.paragraph.mdBold.fontSize,
                fill: theme.color.text.tertiary,
              }
            }
            interval={interval}
            angle={xAxisAngle}
            textAnchor={xAxisTextAnchor}
            height={xAxisLabel ? xAxisHeight + BASE * 3 : xAxisHeight}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            label={
              xAxisLabel
                ? {
                    value: xAxisLabel,
                    position: 'insideBottom',
                    fill: Color.CoreSlate1000,
                    fontSize: theme.text.paragraph.lgBold.fontSize,
                    fontWeight: theme.text.paragraph.lgBold.fontWeight,
                    offset: BASE,
                  }
                : null
            }
          />
          <YAxis
            yAxisId={1}
            orientation='left'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            ticks={yAxisTicks}
            tickFormatter={numbersAxisFormatter ?? formatNumberLarge}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
          />
          <YAxis
            yAxisId={2}
            unit='%'
            orientation='right'
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            ticks={yAxisTicks}
            tickFormatter={(val) =>
              isEmpty(legendNumberOpts)
                ? formatNumberLarge(val)
                : formatNumber(val, legendNumberOpts)
            }
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
            domain={percentageAxisDomain}
          />
          <Tooltip
            isAnimationActive={false}
            content={tooltipContent}
            allowEscapeViewBox={{
              x: allowEscapeViewBoxX,
            }}
          />
          {hideLegend === true ? null : (
            <Legend
              layout='vertical'
              verticalAlign='top'
              align='right'
              formatter={legendFormatter}
              iconType='circle'
              inactiveColor={Color.CoreSlate50}
              // eslint-disable-next-line rulesdir/jsx-click-tracking-ids -- this element doesn't take ID
              onClick={handleLegendClick}
              onMouseEnter={handleLegendMouseEnter}
              onMouseLeave={handleLegendMouseLeave}
              content={
                <ChartLegendContent
                  height={effectiveLegendHeight}
                  topLabel={barsLabel}
                  topKeys={barKeys}
                  bottomGroups={lineGroups}
                  bottomLabel={linesLabel}
                  bottomKeys={lineKeys}
                  layout='vertical'
                  scrollEnabled={scrollEnabled}
                  legendIcons={legendIcons}
                />
              }
            />
          )}
          {barKeys.map((key, idx) => {
            return (
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              <Bar
                yAxisId={1}
                key={key}
                name={keyFormatter == null ? null : keyFormatter(key)}
                isAnimationActive={barIsAnimationActive != null ? barIsAnimationActive : true}
                dataKey={key}
                // This element doesn't take ID and the library types are wrong
                // eslint-disable-next-line rulesdir/jsx-click-tracking-ids, @typescript-eslint/no-explicit-any
                onClick={onDeepLink ? handleDeepLink : null}
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(key)}
                // Add in the stack if its stacked
                stackId={stackedBars ? 'barGroups' : null}
                // Use a stacked color when stacking
                fill={
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  stackedBars ? stackColors[idx % stackColors.length] : colors[idx % colors.length]
                }
                fillOpacity={!legendHovered || hoveredKey == null || hoveredKey === key ? 1 : 0.3}
                stroke={legendHovered && hoveredKey === key ? theme.color.border.active : undefined}
                hide={filteredLegendKeys.length > 0 && !filteredLegendKeys.includes(key)}
                style={{
                  transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
                }}
              >
                {renderBarLabel != null && (
                  <LabelList
                    content={(props) => renderBarLabel(props as RenderLabelProps, 0)}
                    position='center'
                  />
                )}
              </Bar>
            );
          })}
          {lineKeys.map((key, idx) => {
            const fillColor = colors[(stackedBars ? idx : idx + barKeys.length) % colors.length];
            return (
              <Line
                yAxisId={2}
                key={key}
                dataKey={key}
                isAnimationActive={barIsAnimationActive != null ? barIsAnimationActive : true}
                // This element doesn't take ID and the library types are wrong
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(null)}
                fill={fillColor}
                fillOpacity={!legendHovered || hoveredKey == null || hoveredKey === key ? 1 : 0.3}
                stroke={fillColor}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                strokeWidth={legendHovered && hoveredKey === key ? 2 : null}
                hide={filteredLegendKeys.length > 0 && !filteredLegendKeys.includes(key)}
                style={{
                  transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
                }}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartLayout>
  );
};

export default memo(BarAndLineChartWithLegend);

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const TooltipContent: FC<
  ComponentProps<typeof Tooltip> & {
    active?: boolean;
    label?: string;
    payload?: any[];
    hoveredKey: string | null;
    createHeader: (label: string) => string;
    legendFormatter: (value: string) => string;
    keyToUnit: (value: string) => string;
    showZeros?: boolean;
    legendNumberOpts?: Intl.NumberFormatOptions;
  }
> = ({
  active,
  payload,
  label,
  hoveredKey,
  legendFormatter,
  createHeader,
  keyToUnit,
  showZeros,
  legendNumberOpts,
}) => {
  if (!active || payload == null) {
    return null;
  }
  const countLabels = payload
    .slice()
    .map(
      ({ color, dataKey, value }) =>
        typeof value === 'number' &&
        (value > 0 || (showZeros && value === 0)) && (
          <ChartTooltipLabelContainer key={dataKey} active={true} hasActive={hoveredKey != null}>
            <ChartTooltipLabel>
              <Dot style={{ color }} /> {legendFormatter(String(dataKey))}
            </ChartTooltipLabel>
            <ChartTooltipLabel>
              {formatNumber(+value, legendNumberOpts)}
              {keyToUnit(dataKey as string)}
            </ChartTooltipLabel>
          </ChartTooltipLabelContainer>
        ),
    )
    .filter(Boolean);

  return (
    countLabels.length > 0 && (
      <ChartTooltipContainer>
        <ChartTooltipHeader>{createHeader(label)}</ChartTooltipHeader>
        {countLabels}
      </ChartTooltipContainer>
    )
  );
};
